<template>
  <div class="countdown"
       :class="{
        'countdown--active' : isActive,
        'countdown--super-tight' : isSuperTight,
        'countdown--tight' : isTight,
        }">
    <div class="countdown__inner">
      <div class="countdown__circle">
        <svg class="circle-chart" viewBox="-12.5 -12.5 25 25" width="25" height="25" xmlns="http://www.w3.org/2000/svg">
          <circle class="circle__filled" cx="0" cy="0" r="16" stroke="black" stroke-width="0" fill="white" />
          <path class="circle__pie" d="" />
        </svg>
      </div>
      <p class="countdown__copy" v-if="started && !isTight && !isSuperTight"
         v-html="$t('generic.countdown.visible', { time: sessionTimeout })"></p>
      <p class="countdown__copy" v-if="started && (isTight || isSuperTight)"
         v-html="$t('generic.countdown.started', { minutes, seconds })"></p>
    </div>
  </div>
</template>

<script>
import { DateTime } from 'luxon'
import { getJsDate } from '@/utils/date'

const timeLeftInMs = (started, timeToSucceedInMs) => {
  const timeNow = DateTime.local()
  const diff = timeNow.diff(started).toObject()
  return Math.floor(timeToSucceedInMs - diff.milliseconds)
}

export default {
  name: 'CiCountdown',
  props: {
    startOver: {
      type: Function,
      required: true,
    },
    started: {
      type: String | Date,
      required: true,
    },
    sessionTimeout: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      countDownInterval: null,
      minutes: null,
      paymentTimer: null,
      seconds: null,
      timeToPanic: 5,
      timeToWarn: 20,
    }
  },
  created() {
    this.timeToWarn = Math.round((this.sessionTimeout * 2) / 3)
    this.timeToPanic = Math.round((this.sessionTimeout * 5) / 30)
  },
  computed: {
    isActive() {
      return this.started !== null && this.started !== undefined
    },

    isTight() {
      /* istanbul ignore else */
      if (!this.isActive) return
      return Math.max(0, parseInt(this.minutes) < this.timeToWarn)
    },

    isSuperTight() {
      /* istanbul ignore else */
      if (!this.isActive) return
      return Math.max(0, parseInt(this.minutes) < this.timeToPanic)
    },
  },
  methods: {
    onCheckoutStarted(value) {
      if (value === null || value === undefined || !value) {
        this.clearCountdown()
      } else {
        const timeStarted = DateTime.fromJSDate(getJsDate(this.started))
        const timeToSucceedInMs = this.sessionTimeout * 60 * 1000

        /* this interval updates minutes & seconds left every 1000ms */
        clearInterval(this.countDownInterval)
        this.countDownInterval = setInterval(() => {
          this.drawCountdown(timeStarted, timeToSucceedInMs)
        }, 1000)

        /* start timeout until show session expired dialog */
        const diffInMs = timeLeftInMs(timeStarted, timeToSucceedInMs)
        this.startPaymentTimer(diffInMs)
      }
    },

    drawCountdown(timeStarted, timeToSucceedInMs) {
      const diffInMs = timeLeftInMs(timeStarted, timeToSucceedInMs)
      const seconds = Math.floor((diffInMs / 1000) % 60)
      const minutes = Math.floor((diffInMs / (1000 * 60)) % 60)

      this.minutes = minutes < 10 ? '0' + minutes : minutes
      this.seconds = seconds < 10 ? '0' + seconds : seconds

      const passed = timeToSucceedInMs - diffInMs
      const onePercent = timeToSucceedInMs / 100
      this.drawCircle(passed / onePercent)
    },

    drawCircle(percent) {
      const radius = 15
      const angle = percent * 3.6 // 1% ~ 3.6deg
      const coords = [
        radius * Math.cos((Math.PI * angle) / 180),
        radius * Math.sin((Math.PI * angle) / 180),
      ]
      const path =
        'M0,0 L' +
        radius +
        ',0 A' +
        radius +
        ',' +
        radius +
        ' 0 1,1 ' +
        coords[0] +
        ',' +
        coords[1] +
        ' Z'

      this.$el.querySelector('.circle__pie').setAttribute('d', path)
    },

    startPaymentTimer(timeLeft) {
      clearTimeout(this.paymentTimer)
      this.paymentTimer = setTimeout(() => {
        /* clear interval and timeout */
        this.clearCountdown()
        this.startOver()
        /* show user dialog */
        this.$modal.show('dialog', {
          buttons: {
            primary: {
              active: true,
              classes: 'button--primary button--center',
              text: null,
            },
            secondary: { active: false },
          },
          content: this.$t('modals.CiSessionExpired.contentAppRestart'),
          title: this.$t('modals.CiSessionExpired.title'),
        })
      }, timeLeft)
    },

    clearCountdown() {
      clearInterval(this.countDownInterval)
      clearTimeout(this.paymentTimer)
      this.minutes = null
      this.seconds = null
    },
  },
  watch: {
    started: {
      handler: 'onCheckoutStarted',
      immediate: true,
    },
  },
}
</script>
<style lang="scss">
.countdown__time-left {
  @include font('gotham_mediumregular');

  display: inline-block;
  padding: 0;
  width: 55px;

  &.countdown__time-left--static {
    padding-right: 4px;
    width: auto;
  }
}

.countdown__bold {
  @include font('gotham_mediumregular');
}
</style>
<style lang="scss" scoped>
.countdown {
  background: transparent;
  position: absolute;
  text-align: center;
  top: 60px;
  transform: translate(0, calc(-100% - 60px));
  transition: all 0s ease-in-out 0s;
  width: 100%;

  @include media-breakpoint-up(md) {
    top: 55px;
    transform: translate(0, calc(-100% - 55px));
    width: calc(100% - 70px);
  }

  @include media-breakpoint-up(lg) {
    top: 25px;
    transform: translate(0, calc(-100% - 25px));
  }

  @include media-breakpoint-up(xl) {
    top: 60px;
    transform: translate(0, calc(-100% - 60px));
  }

  .countdown__inner {
    background: $primary-green;
    border-radius: 37.5px;
    color: white;
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 18px 8px 8px;
    position: relative;
    width: auto;

    @include media-breakpoint-up(md) {
      display: inline-flex;
    }

    .countdown__copy {
      margin: 0;
      padding: 0;
    }
  }

  &.countdown--active {
    transform: translate(0, 0);
    transition: all 0.5s ease-in-out 3s;
  }

  &.countdown--tight {
    .countdown__inner {
      background: $primary-orange;

      .countdown__circle svg path {
        fill: $primary-orange;
      }
    }
  }

  &.countdown--super-tight {
    .countdown__inner {
      background: $primary-red;

      .countdown__circle svg path {
        fill: $primary-red;
      }
    }
  }

  .countdown__circle {
    position: relative;
    display: block;
    height: 25px !important;
    width: 25px !important;
    transform-origin: 50% 50%;
    transform: rotate(-90deg);
    border-radius: 50%;
    overflow: hidden;
    background: transparent;
    border: 1px solid white;
    margin-right: 10px;
    flex-shrink: 0;

    svg {
      background: white;
      border-radius: 50%;
      left: 50%;
      position: absolute;
      top: 50%;
      transform: translate(-50%, -50%);
    }

    .circle__filled {
      border-radius: 50%;
      max-height: 25px;
      max-width: 25px;
    }

    .circle__pie {
      fill: $primary-green;
    }
  }
}
</style>
